import getConversationTailoredXpData from './getConversationTailoredXpData';
import { MAX_ITEMS_ALLOWED_TO_RETRIEVE } from '../actions/loadConversation';
import createEmptyConversationItemParts from '../utils/createEmptyConversationItemParts';
import preserveItemDataInCache from '../utils/preserveItemDataInCache';
import initializeUndefinedItemProperties, {
    itemMandatoryProperties,
} from '../utils/initializeUndefinedItemProperties';
import { ObservableMap } from 'mobx';
import { ClientItemId, MailboxInfo } from 'owa-client-ids';
import { MruCache } from 'owa-mru-cache';
import ConversationNode from 'owa-service/lib/contract/ConversationNode';
import ConversationSortOrder from 'owa-service/lib/contract/ConversationSortOrder';
import GetConversationItemsResponseMessage from 'owa-service/lib/contract/GetConversationItemsResponseMessage';
import Item from 'owa-service/lib/contract/Item';
import Message from 'owa-service/lib/contract/Message';
import { trace } from 'owa-trace';
import { action, select } from 'satcheljs/lib/legacy';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import {
    conversationCache,
    mailStore,
    CollectionChange,
    ConversationItemParts,
    ConversationReadingPaneNode,
    LocalLieState,
    ClientItem,
} from 'owa-mail-store';
import tryRemoveFromMailStoreItems, {
    RemoveItemSource,
} from 'owa-mail-actions/lib/triage/tryRemoveFromMailStoreItems';

export interface ItemMandatoryPropertiesCollectionChange {
    isReadChangedToUnread: string[];
}

interface IdProvider<T> {
    (object: T): string;
}

const CHRONOLOGICAL_NEWEST_ON_BOTTOM_SORT_ORDER_NAME = 'ChronologicalNewestOnBottom';
const PLACEHOLDER_NODE_INTERNET_MESSAGE_ID = 'PLACEHOLDER_NODE_INTERNET_MESSAGE_ID';

const conversationNodeIdProvider: IdProvider<ConversationNode> = (
    conversationNode: ConversationNode
) => {
    return conversationNode.InternetMessageId;
};

const itemIdProvider: IdProvider<ClientItem> = (item: ClientItem) => {
    return item.ItemId.Id;
};

/** Generate array of ids from array of objects. */
function generateIdsFromObjects<T>(objects: T[], idProvider: IdProvider<T>): string[] {
    const ids: string[] = [];
    objects.forEach(object => {
        ids.push(idProvider(object));
    });
    return ids;
}

/** Compare array of ids between old and new. */
function checkIfDifferentIds(oldIds: string[], newIds: string[]): boolean {
    if (oldIds.length != newIds.length) {
        return true;
    }

    for (let i = 0; i < oldIds.length; i++) {
        if (oldIds[i] != newIds[i]) {
            return true;
        }
    }

    return false;
}

/** Get the old ids that are not included in the array of new ids */
function getAdditionalOldIds(oldIds: string[], newIds: string[]): string[] {
    const additionalOldIds: string[] = [];
    oldIds.forEach(oldId => {
        if (newIds.indexOf(oldId) < 0) {
            additionalOldIds.push(oldId);
        }
    });
    return additionalOldIds;
}

/** Clear and refill with new ids if they are different from old ids. */
/** Also return the old ids that are not included in the new ids. */
function resetIdsIfDifferent<T>(
    oldIds: string[],
    objects: T[],
    idProvider: IdProvider<T>
): [boolean, string[]] {
    const newIds = generateIdsFromObjects(objects, idProvider);
    if (checkIfDifferentIds(oldIds, newIds)) {
        const additionalOldIds = getAdditionalOldIds(oldIds, newIds);
        // Clear and refill.
        const oldCount = oldIds.length;
        oldIds.splice(0, oldCount, ...newIds);
        return [true, additionalOldIds];
    }
    return [false, []];
}

function checkIfCollectionChanged<T>(change: CollectionChange<T>): boolean {
    return change.added.length > 0 || change.removed.length > 0 || change.modified.length > 0;
}

function checkIfItemMandatoryPropertiesCollectionChanged(
    change: ItemMandatoryPropertiesCollectionChange
): boolean {
    return change.isReadChangedToUnread.length > 0;
}

function cleanUpItem(itemId: string, itemsState: ObservableMap<string, ClientItem>) {
    tryRemoveFromMailStoreItems(itemId, RemoveItemSource.ConversationItemParts);
}

function cleanUpConversationNode(
    nodeId: string,
    conversationNodesState: ObservableMap<string, ConversationReadingPaneNode>,
    itemsState: ObservableMap<string, ClientItem>
) {
    const nodeToRemove = conversationNodesState.get(nodeId);
    nodeToRemove.itemIds.forEach(itemId => {
        cleanUpItem(itemId, itemsState);
    });
    conversationNodesState.delete(nodeId);
}

function checkItemMandatoryPropertiesChange(
    nodeId: string,
    newItems: Item[],
    itemsState: ObservableMap<string, ClientItem>,
    nodeIdCollctionWithItemMandatoryPropertiesChanged: ItemMandatoryPropertiesCollectionChange
) {
    for (const newItem of newItems) {
        const oldItem = itemsState.get(newItem.ItemId.Id);
        if (oldItem) {
            if ((<Message>oldItem).IsRead && !(<Message>newItem).IsRead) {
                nodeIdCollctionWithItemMandatoryPropertiesChanged.isReadChangedToUnread.push(
                    nodeId
                );
            }
        }
    }
}

/** @returns collection change of ids for conversation nodes. */
function mergeConversationNodes(
    oldIds: string[],
    newConversationNodes: ConversationNode[],
    conversationNodesState: ObservableMap<string, ConversationReadingPaneNode>,
    itemsState: ObservableMap<string, ClientItem>,
    conversationSortOrder: ConversationSortOrder,
    conversationId: ClientItemId
): [CollectionChange<string>, ItemMandatoryPropertiesCollectionChange] {
    preservePendingNodesIfNecessary(
        oldIds,
        newConversationNodes,
        conversationNodesState,
        itemsState,
        conversationSortOrder
    );
    const additionalOldIds = resetIdsIfDifferent(
        oldIds,
        newConversationNodes,
        conversationNodeIdProvider
    )[1];
    const nodeIdCollectionChange: CollectionChange<string> = {
        added: [],
        removed: [],
        modified: [],
    };
    const nodeIdCollectionWithItemMandatoryPropertiesChanged: ItemMandatoryPropertiesCollectionChange = {
        isReadChangedToUnread: [],
    };

    // Merge each new node with the old.
    newConversationNodes.forEach(newNode => {
        const nodeId = conversationNodeIdProvider(newNode);
        const oldNode = conversationNodesState.get(nodeId);
        let nodeToMerge: ConversationReadingPaneNode;

        if (!oldNode) {
            // This is the case where a new node is added.
            // Create and save a node to merge.
            conversationNodesState.set(nodeId, {
                conversationId: conversationId.Id,
                internetMessageId: nodeId,
                itemIds: [],
                quotedTextList: newNode.QuotedTextList,
                hasQuotedText: newNode.HasQuotedText,
                isQuotedTextChanged: newNode.IsQuotedTextChanged,
                quotedTextState: newNode.QuotedTextState,
                localLieState: LocalLieState.None,
                parentInternetMessageId: newNode.ParentInternetMessageId,
            });

            nodeToMerge = conversationNodesState.get(nodeId);
            nodeIdCollectionChange.added.push(nodeId);
        } else {
            // We need to merge the parent internet message id for the bus stops to be refreshed. This is because
            // the server will not send down the parent internet message id if the node is the last node in the GCI.
            if (newNode.ParentInternetMessageId) {
                oldNode.parentInternetMessageId = newNode.ParentInternetMessageId;
            }

            if (newNode.QuotedTextList) {
                oldNode.quotedTextList = newNode.QuotedTextList;
            }

            if (newNode.QuotedTextState) {
                oldNode.quotedTextState = newNode.QuotedTextState;
            }

            if (newNode.HasQuotedText) {
                oldNode.hasQuotedText = true;
            }

            if (newNode.IsQuotedTextChanged) {
                oldNode.isQuotedTextChanged = true;
            }

            nodeToMerge = oldNode;
        }

        const oldItemIds = nodeToMerge.itemIds;
        const [isItemArrayDifferent, additionalOldItemIds] = resetIdsIfDifferent(
            oldItemIds,
            newNode.Items,
            itemIdProvider
        );
        if (oldNode && isItemArrayDifferent) {
            // This is the case where an old node is modified.
            nodeIdCollectionChange.modified.push(nodeId);
        }
        if (oldNode && !isItemArrayDifferent) {
            checkItemMandatoryPropertiesChange(
                nodeId,
                newNode.Items,
                itemsState,
                nodeIdCollectionWithItemMandatoryPropertiesChanged
            );
        }

        // Merge items for each new node.
        mergeItems(
            oldItemIds,
            newNode.Items,
            additionalOldItemIds,
            itemsState,
            conversationId.mailboxInfo
        );
    });

    // Clean up any additional old node.
    additionalOldIds.forEach(additionalOldId => {
        // This is the case where an old node is removed.
        cleanUpConversationNode(additionalOldId, conversationNodesState, itemsState);
        nodeIdCollectionChange.removed.push(additionalOldId);
    });

    return [nodeIdCollectionChange, nodeIdCollectionWithItemMandatoryPropertiesChanged];
}

/** @returns [whether item collection has been changed, whether item collection contains item changed from read to unread] */
function mergeItems(
    oldIds: string[],
    newItems: Item[],
    additionalOldIds: string[],
    itemsState: ObservableMap<string, ClientItem>,
    mailboxInfo: MailboxInfo
) {
    // Merge each new item with the old.
    newItems.forEach(newItem => {
        const newItemId = newItem.ItemId.Id;
        const oldItem = itemsState.get(newItemId);

        // Initialize required properties that could be undefined so they're observable in the store.
        initializeUndefinedItemProperties(newItem);

        if (!newItem.ContainsOnlyMandatoryProperties) {
            // Only cache new item from response if new item contains complete list of properties.
            // Sync state should guarantee that if we already have an item, the GCI response only contains mandatory properties.
            // But some scenarios (TxP) ignore the sync state, so we should preserve any item data we may have fetched so it doesn't get overwritten.
            if (oldItem) {
                preserveItemDataInCache(newItem, oldItem);
            }

            itemsState.set(newItemId, {
                TranslationData: {
                    isShowingTranslation: false,
                    isTranslatable: false,
                    isTranslating: false,
                    shouldGetFeedback: false,
                    isShowingForwardContentTranslation: false,
                    isForwardContentTranslatable: false,
                    isShowingSubjectTranslation: false,
                    manuallyTranslated: false,
                    userLanguage: null,
                },
                adaptiveCardDetails: null,
                ...newItem,
                MailboxInfo: mailboxInfo,
                SmartReplyData: null,
                SmartTimeData: null,
                SmartTaskData: null,
                SmartTimeExtendedProperty: null,
                AttachmentSuggestions: null,
                ContextualAttachmentSuggestions: null,
                smartPillFeedbackSubmitted: false,
                YammerThreadId: null,
                itemCLPInfo: null,
            });
        } else {
            if (oldItem) {
                // Update mandatory properties on old item if it exists in cache.
                itemMandatoryProperties.forEach(property => {
                    if (typeof newItem[property.name] !== 'undefined') {
                        oldItem[property.name] = newItem[property.name];
                    }
                });
            } else {
                // Remove item id from ConversationNode.ItemIds if the old item isn't cached and new item only contains mandatory properties from response.
                oldIds.splice(oldIds.indexOf(newItemId), 1);
            }
        }
    });

    // Clean up any additional old item.
    additionalOldIds.forEach(additionalOldId => {
        cleanUpItem(additionalOldId, itemsState);
    });
}

function handleNodesWithDuplicateIds(
    conversationNodesState: ObservableMap<string, ConversationReadingPaneNode>,
    conversationNodes: ConversationNode[],
    conversationSortOrder: ConversationSortOrder,
    conversationId: ClientItemId
) {
    const nodeIdMap: { [nodeId: string]: boolean } = {};
    const nodeCount = conversationNodes.length;
    const isNewestOnBottom =
        conversationSortOrder == CHRONOLOGICAL_NEWEST_ON_BOTTOM_SORT_ORDER_NAME;
    let index = isNewestOnBottom ? 0 : nodeCount - 1;
    const increment = isNewestOnBottom ? 1 : -1;
    while (index >= 0 && index < nodeCount) {
        const node = conversationNodes[index];
        const existingNode = conversationNodesState.get(node.InternetMessageId);
        if (
            (existingNode && existingNode.conversationId != conversationId.Id) ||
            nodeIdMap[node.InternetMessageId]
        ) {
            // If there's node with the same InternetMessageId from a different conversation or
            // there's a node with the same InternetMessageId in the same conversation,
            // then append ItemId to InternetMessageId to make it unique
            node.InternetMessageId += node.Items[0].ItemId.Id;

            // Update InternetMessageId of each item in the node
            node.Items.forEach(
                item => ((<Message>item).InternetMessageId = node.InternetMessageId)
            );
        } else {
            nodeIdMap[node.InternetMessageId] = true;
        }

        index += increment;
    }
}

function preservePendingNodesIfNecessary(
    oldIds: string[],
    newConversationNodes: ConversationNode[],
    conversationNodesState: ObservableMap<string, ConversationReadingPaneNode>,
    itemsState: ObservableMap<string, ClientItem>,
    conversationSortOrder: ConversationSortOrder
) {
    if (oldIds.length > 0) {
        // Check if last node is pending
        const isNewestOnBottom =
            conversationSortOrder == CHRONOLOGICAL_NEWEST_ON_BOTTOM_SORT_ORDER_NAME;
        const indexOfLastNode = isNewestOnBottom ? oldIds.length - 1 : 0;
        const lastNode = conversationNodesState.get(oldIds[indexOfLastNode]);

        if (lastNode.localLieState == LocalLieState.Pending) {
            // If so, see if the response collection contains a node with a matching IMI
            let shouldPreserveLocalLie = true;
            let matchingNode = findMatchingNode(newConversationNodes, lastNode);
            if (matchingNode) {
                if (matchingNode.Items[0].IsDraft) {
                    // If the matching node is a draft, remove it since we'll preserve the local lie node.
                    newConversationNodes.splice(newConversationNodes.indexOf(matchingNode), 1);
                } else {
                    // If the matching node is a normal item, keep it and update the local lie node.
                    shouldPreserveLocalLie = false;
                    updateLocalLieNode(lastNode, matchingNode);
                    deleteLocalLieSentTime(lastNode, itemsState);
                }
            }
            if (shouldPreserveLocalLie) {
                // Set the matching node to have the same IMI and itemId so the local lie node persists.
                matchingNode = {
                    InternetMessageId: lastNode.internetMessageId,
                    Items: [itemsState.get(lastNode.itemIds[0])],
                } as ConversationNode;

                if (isNewestOnBottom) {
                    newConversationNodes.push(matchingNode);
                } else {
                    newConversationNodes.unshift(matchingNode);
                }
            }
        }
    }
}

function updateLocalLieNode(
    localLieNode: ConversationReadingPaneNode,
    matchingNode: ConversationNode
) {
    localLieNode.localLieState = LocalLieState.Complete;
    localLieNode.hasQuotedText = matchingNode.HasQuotedText;
    localLieNode.isQuotedTextChanged = matchingNode.IsQuotedTextChanged;
    localLieNode.quotedTextList = matchingNode.QuotedTextList;
}

function deleteLocalLieSentTime(
    localLieNode: ConversationReadingPaneNode,
    itemsState: ObservableMap<string, ClientItem>
) {
    const item = itemsState.get(localLieNode.itemIds[0]);
    if (item) {
        delete item.localLieSentTime;
    }
}

/**
 * This function is used to find the matching node in the new conversation nodes for the pending node.
 * Here is the description for the local lie(pending node) scenario:
 * 1. When inline compose is opened, a CreateItem request is triggered and the response contains
 *    itemId of this draft.
 * 2. Notification triggers a GCI to get all the nodes for this conversation, the response contains
 *    the internetMessageId of this draft item.
 * 3. User click send and markConversationNodePendingNotPending is triggered to mark the node as pending.
 *    If the GCI response of #2 doesn't return yet, a local lie node is created with a place holder node id.
 * 4. When undo send is enabled, during the undo send period, if the GCI response of #2 is returned, we need
 *    to find the matching node in the new conversation nodes by itemId and preserve the pending node.
 *    If not, there will be two nodes with the same itemId existing in the conversationNodes list.
 */
function findMatchingNode(
    newConversationNodes: ConversationNode[],
    pendingNode: ConversationReadingPaneNode
): ConversationNode {
    if (pendingNode.internetMessageId == PLACEHOLDER_NODE_INTERNET_MESSAGE_ID) {
        if (pendingNode.itemIds && pendingNode.itemIds.length > 0) {
            return findNodeByItemId(newConversationNodes, pendingNode.itemIds[0]);
        } else {
            return null;
        }
    } else {
        return findNodeById(newConversationNodes, pendingNode.internetMessageId);
    }
}

function findNodeById(conversationNodes: ConversationNode[], nodeId: string): ConversationNode {
    for (const conversationNode of conversationNodes) {
        if (conversationNodeIdProvider(conversationNode) == nodeId) {
            return conversationNode;
        }
    }
    return null;
}

function findNodeByItemId(conversationNodes: ConversationNode[], itemId: string): ConversationNode {
    for (const conversationNode of conversationNodes) {
        for (const item of conversationNode.Items) {
            if (item.ItemId.Id == itemId) {
                return conversationNode;
            }
        }
    }
    return null;
}

export interface ParseConversationResponseState {
    conversations: MruCache<ConversationItemParts>;
    conversationNodes: ObservableMap<string, ConversationReadingPaneNode>;
    items: ObservableMap<string, ClientItem>;
}

export type ConversationReadyToLoadStitch = (
    conversationId: string,
    nodeIdCollectionChange?: CollectionChange<string>
) => void;

export let ConversationReadyToLoadStitchName = 'conversationReadyToLoadStitch';

export type UpdateItemMandatoryPropertiesStitchAction = (
    conversationId: string,
    nodeIdCollectionWithItemMandatoryPropertiesChanged: ItemMandatoryPropertiesCollectionChange
) => void;

export let UpdateItemMandatoryPropertiesStitchActionName =
    'updateItemMandatoryPropertiesStitchAction';

export default action('parseConversationResponse')(
    select<ParseConversationResponseState>({
        conversations: () => conversationCache,
        conversationNodes: () => mailStore.conversationNodes,
        items: () => mailStore.items,
    })(function parseConversationResponse(
        responseMessage: GetConversationItemsResponseMessage,
        conversationSortOrder: ConversationSortOrder,
        conversationId: ClientItemId,
        state?: ParseConversationResponseState
    ) {
        const conversationRawId = conversationId.Id;
        let conversationToLoad = state.conversations.get(conversationRawId);
        if (!conversationToLoad) {
            // If conversation is not in cache, it must be purged. In this case, we need to create an empty one.
            conversationToLoad = createEmptyConversationItemParts(
                conversationId,
                conversationSortOrder
            );
        }

        // Save empty conversationItemPart or touch conversationCache
        state.conversations.add(conversationRawId, conversationToLoad);
        conversationToLoad = state.conversations.get(conversationRawId);

        // Property hasLoadFailed is undefined implies we create an empty ConversationItemParts due to no cache or previous cache failed,
        // update hasLoadFailed if it's undefined, otherwise, keep the cached value as is.
        const hasLoadFailed = !(responseMessage && responseMessage.ResponseClass == 'Success');
        if (conversationToLoad.loadingState.hasLoadFailed === undefined) {
            conversationToLoad.loadingState.hasLoadFailed = hasLoadFailed;
        }

        if (conversationToLoad.loadingState.hasLoadFailed) {
            // Notify reading pane that load has failed.
            raiseAction<ConversationReadyToLoadStitch>(ConversationReadyToLoadStitchName)(
                conversationRawId
            );
        }

        // Parse Success conversation response
        if (!hasLoadFailed) {
            const conversationResponse = responseMessage.Conversation;
            if (!conversationResponse.ConversationNodes) {
                trace.warn(
                    `GCI response has no ConversationNodes, but TotalConversationNodesCount is ${
                        conversationResponse.TotalConversationNodesCount
                    }`
                );
                // Even though the response was successful, we don't have anything to show.
                // Set hasLoadFailed and notify reading pane so we show the error message.
                conversationToLoad.loadingState.hasLoadFailed = true;
                raiseAction<ConversationReadyToLoadStitch>(ConversationReadyToLoadStitchName)(
                    conversationRawId
                );
                return;
            }

            // Only update SyncState when the conversation node counts match.
            // When send mail to self, it is possible that the conversation table is updated according to sent item, but the message is not delivered yet.
            // In this case the syncState might contain new message id but GCI response doesn't really contain this conversationNode.
            if (
                conversationResponse.ConversationNodes.length ==
                conversationResponse.TotalConversationNodesCount
            ) {
                conversationToLoad.syncState = conversationResponse.SyncState;
            } else {
                const nodeCount = conversationResponse.ConversationNodes.length;
                const totalNodeCount = conversationResponse.TotalConversationNodesCount;
                trace.warn(
                    `Count of conversationNodes (${nodeCount}) returned from GetConversationItems doesn't match TotalConversationNodesCount (${totalNodeCount})`
                );
            }

            // Update load more value
            conversationToLoad.canLoadMore =
                conversationResponse.RemainingConversationNodesCount > 0 ||
                (conversationToLoad.maxItemsToReturn < MAX_ITEMS_ALLOWED_TO_RETRIEVE &&
                    conversationResponse.ConversationNodes &&
                    !(
                        conversationResponse.ConversationNodes.length <
                        conversationToLoad.maxItemsToReturn
                    ));
            conversationToLoad.isLoadMoreInProgress = false;

            conversationToLoad.canDelete = conversationResponse.CanDelete || null;

            // Handle nodes with duplicate ids.
            // Append ItemId to InternetMessageId to make it unique whenever a duplicate InternetMessageId is detected.
            // This usually happens to system-generated emails wrongfully using the same InternetMessageId.
            // Note that it doesn't handle the case where the first node/conversation with duplicate InternetMessageId is hard deleted,
            // but it's a corner case and there's no other way to correlate.
            handleNodesWithDuplicateIds(
                state.conversationNodes,
                conversationResponse.ConversationNodes,
                conversationSortOrder,
                conversationId
            );

            // Fetch TxP data if necessary
            getConversationTailoredXpData(
                conversationResponse.ConversationNodes,
                conversationSortOrder
            );

            // Merge nodes.
            const [
                nodeIdCollectionChanged,
                nodeIdCollectionWithItemMandatoryPropertiesChanged,
            ] = mergeConversationNodes(
                conversationToLoad.conversationNodeIds,
                conversationResponse.ConversationNodes,
                state.conversationNodes,
                state.items,
                conversationSortOrder,
                conversationId
            );

            // Notify reading pane to update readingPaneStore if there's collection change.
            if (checkIfCollectionChanged(nodeIdCollectionChanged)) {
                raiseAction<ConversationReadyToLoadStitch>(ConversationReadyToLoadStitchName)(
                    conversationRawId,
                    nodeIdCollectionChanged
                );
            }

            // If has item changed to unread from notification, notify reading pane to expand the item part.
            // If has item changed to read from notification, leave the item part as is.
            if (
                checkIfItemMandatoryPropertiesCollectionChanged(
                    nodeIdCollectionWithItemMandatoryPropertiesChanged
                )
            ) {
                raiseAction<UpdateItemMandatoryPropertiesStitchAction>(
                    UpdateItemMandatoryPropertiesStitchActionName
                )(conversationRawId, nodeIdCollectionWithItemMandatoryPropertiesChanged);
            }
        }
    })
);
